import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import RedLoop from "assets/svg/SearchIconRed.svg";
import { SearchContainer, ResultsGrid, NoResults, SearchHeader, SearchButton, InputContainer, ImgStyled } from "./Search.styled";
import SearchCard from "components/Molecules/SearchCard/SearchCard";
import { SearchResult } from "components/Molecules/SearchCard/SearchCardModel";
import CustomText from "components/Atoms/CustomText/CustomText";
import { TextType } from "components/Atoms/CustomText/CustomTextModel";
import InputAtom from "components/Atoms/InputAtom/InputAtom";
import Pagination from "components/Atoms/Paginations/Pagination";
import { COLORS } from "constants/variables";
import { siteSearchPages } from "config/SiteSearchPagesConfig";
import siteSearchService from "services/siteSearchService";
import { normalizeNewsSearch } from "utils/newsSearch";
import { accessibilityBiggerCursorSelector } from "slices/UISlice/uiSliceSelectors";

const PAGE_SIZE = 10;

const Search = () => {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const submittedQuery = (searchParams.get("q") ?? "").trim();
  const requestedPage = Number(searchParams.get("page") ?? 1);
  const page = Number.isSafeInteger(requestedPage) && requestedPage > 0 ? requestedPage : 1;
  const locale = i18n.language.split("-")[0];
  const biggerCursor = useSelector(accessibilityBiggerCursorSelector);
  const topRef = useRef<HTMLDivElement>(null);
  const [draftQuery, setDraftQuery] = useState(submittedQuery);
  const [revision, setRevision] = useState(0);
  const [response, setResponse] = useState({
    query: "", locale: "", loading: false, error: false, incomplete: false, results: [] as SearchResult[],
  });

  useEffect(() => { setDraftQuery(submittedQuery); }, [submittedQuery]);

  useEffect(() => {
    if (!submittedQuery) return;
    const controller = new AbortController();
    setResponse({ query: submittedQuery, locale, loading: true, error: false, incomplete: false, results: [] });
    siteSearchService.search(submittedQuery, locale, controller.signal)
      .then((data) => {
        if (controller.signal.aborted) return;
        setResponse({ query: submittedQuery, locale, loading: false, error: false, ...data });
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setResponse({ query: submittedQuery, locale, loading: false, error: true, incomplete: false, results: [] });
      });
    return () => controller.abort();
  }, [submittedQuery, locale, revision]);

  const results = useMemo(() => {
    if (!submittedQuery) return [];
    const query = normalizeNewsSearch(submittedQuery);
    const pages = siteSearchPages.map((entry): SearchResult => ({
      id: `page-${entry.url}`, title: t(entry.titleKey).replace(/\s+/g, " ").trim(),
      url: entry.url, content: "", categoryKey: "pages",
    })).filter((entry) => normalizeNewsSearch(entry.title).includes(query));
    return [...pages, ...response.results];
  }, [submittedQuery, response.results, t]);

  const isLoading = Boolean(submittedQuery) && (response.loading || response.query !== submittedQuery || response.locale !== locale);
  const totalPages = Math.ceil(results.length / PAGE_SIZE);
  const currentPage = Math.min(page, Math.max(1, totalPages));
  const currentResults = results.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const query = draftQuery.trim();
    setSearchParams(query ? { q: query } : {});
    if (query === submittedQuery) setRevision((value) => value + 1);
  };

  return (
    <SearchContainer ref={topRef}>
      <SearchHeader>
        <CustomText type={TextType.H2} color={COLORS.RaisinBlack} text={t("components.search.title")} />
        <InputContainer>
          <form role="search" onSubmit={handleSearch} aria-label={t("components.search.title")}>
            <InputAtom startAdornment={<ImgStyled src={RedLoop} alt="" />}
              placeholder={t("components.search.searchPlaceholder")}
              endAdornment={<SearchButton type="submit" $biggerCursor={biggerCursor}>
                <CustomText text={t("view.newsAndMedia.officialAnnouncements.search")} type={TextType.H5} />
              </SearchButton>}
              isSearchBar value={draftQuery}
              onChange={(event) => {
                const value = event.target.value;
                setDraftQuery(value);
                if (!value.trim()) setSearchParams({}, { replace: true });
              }} />
          </form>
        </InputContainer>
      </SearchHeader>

      {!submittedQuery ? (
        <NoResults role="status"><CustomText type={TextType.P1} text={t("search.enterSearchTerm")} /></NoResults>
      ) : isLoading ? (
        <NoResults role="status" aria-live="polite"><CustomText type={TextType.P1} text={t("common.loading")} /></NoResults>
      ) : response.error ? (
        <NoResults role="alert"><CustomText type={TextType.P1} text={t("components.search.error")} /></NoResults>
      ) : (
        <>
          {response.incomplete && <NoResults role="alert"><CustomText type={TextType.P1} text={t("components.search.incomplete")} /></NoResults>}
          {results.length ? (
            <>
              <div role="status" aria-live="polite"><CustomText type={TextType.P2}
                text={t("components.search.resultCount", { count: results.length, query: submittedQuery })} /></div>
              <ResultsGrid>{currentResults.map((result) => <SearchCard key={result.id} result={result} />)}</ResultsGrid>
              {totalPages > 1 && <Pagination totalPages={totalPages} currentPage={currentPage} onPageChange={(nextPage) => {
                setSearchParams({ q: submittedQuery, page: String(nextPage) });
                topRef.current?.scrollIntoView({ block: "start", behavior: "auto" });
              }} />}
            </>
          ) : !response.incomplete && (
            <NoResults role="status" aria-live="polite"><CustomText type={TextType.P1}
              text={t("components.search.noResults", { query: submittedQuery })} /></NoResults>
          )}
        </>
      )}
    </SearchContainer>
  );
};

export default Search;
